/* global setTimeout, NodeJS, clearTimeout, console */

import { DocumentPartWithStatus } from "../../api/models/document-part-with-status";
import { IContextualMenuProps, Spinner, SpinnerSize } from "@fluentui/react";
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { DocumentRoot } from "../../api/models/document-root";
import { fetchDocumentPath } from "../../api/document/Documents";
import { DEFAULT_FOLDER_CREATION } from "../document/WithApiFileLocationProvider";
import { IconButton } from "@fluentui/react/lib/Button";
import { generateIdentifier } from "../../utils/helpers/RobotFramework";
import { getWorkflowStatus, Tooltip, TreeViewDataItem } from "./DocumentPartTree";
import { WorkflowStatus } from "../../api/models/workflow-status";

function iconClassName(item: TreeViewDataItem) {
  if (item.nodeType == "pagination") return;
  if (item.nodeType == "section") return "k-icon k-i-excel";
  return "k-icon k-i-word";
}

function checkSignificantStatus(item: WorkflowStatus) {
  return !(item?.isSignificant || item?.significant);
}

function iconCertification(item: TreeViewDataItem, documentPartWithStatus: DocumentPartWithStatus[]) {
  const itemState = getWorkflowStatus(item.key, documentPartWithStatus);
  if (itemState == null) {
    return "loading_status";
  }
  if (checkSignificantStatus(itemState)) {
    return "ms-significant_icon";
  }
  switch (itemState?.workflowStatusCode) {
    case "20":
      return "ms-in_progress_icon";
    case "30":
      // eslint-disable-next-line no-case-declarations
      const element: string[] = itemState.actions;
      if (element?.includes("APPROVE")) {
        return "ms-pending_withpermission_icon";
      } else {
        return "ms-pending_withoutpermission_icon";
      }
    case "40":
      return "ms-rejection_icon";
    case "50":
      return "ms-submission_icon";
    default:
      return "ms-initial_state_icon";
  }
}

const RenderMenuIcon = () => <span />;

export interface TreeItemRowProps {
  item: TreeViewDataItem;
  itemHierarchicalIndex: string;
  isSelected: boolean;
  isDraggable?: boolean;
  getIconButtonMenuProps?: (
    item: any,
    itemHierarchicalIndex: string,
    documentPartWhithStatus: DocumentPartWithStatus[]
  ) => IContextualMenuProps;
  onClick?: React.MouseEventHandler;
  hasReadPermissions?: boolean;
  onChangePage?: (key: string, page: string) => React.MouseEventHandler;
  fetchExpandedNode?: (id: string, isReload: boolean, page: number) => void;
  isLinkExisting?: boolean;
  certificationOptions?: (item: TreeViewDataItem) => IContextualMenuProps;
  isOutlineRibbon?: boolean;
  isOpenOutlineRibbon?: boolean;
  azureDefaultSite?: string;
  setSelectedItem?: (item: TreeViewDataItem) => void;
  elementToUpdate?: Dispatch<SetStateAction<DocumentRoot[]>>;
  documentPartWhithStatus?: DocumentPartWithStatus[];
  setDocumentPartWhithStatus?: (documentPartWhithStatus: DocumentPartWithStatus) => void;
  pathInfo?: { setPathForItem: (itemId: string, path: string) => void; getPathForItem: (itemId: string) => string };
}

export const TreeItemRow = (props: TreeItemRowProps) => {
  const multiLinked = props.item.data.multiLinked ?? props.item.data.documentPart?.multiLinked;
  const menuProps =
    props.item.hasReadPermissions &&
    !props.isDraggable &&
    !props.isLinkExisting &&
    props.getIconButtonMenuProps?.(props.item, props.itemHierarchicalIndex, props.documentPartWhithStatus);

  const documentName = props.item.text.replace(/\.docx$/, "");

  const [timeoutInst, setTimeoutInst] = React.useState<NodeJS.Timeout>(null);
  const [isLoadingPath, setIsLoadingPath] = React.useState<boolean>(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    positionX: number;
    positionY: number;
  } | null>(null);

  const handleTooltipCreation = useCallback(
    (e: React.MouseEvent) => {
      if (!props.item.hasReadPermissions) return;
      setTooltipPosition((s) => ({ ...s, positionX: e.clientX, positionY: e.clientY }));
      if (!timeoutInst && !props.pathInfo.getPathForItem(props.item.key)) {
        setTimeoutInst(
          setTimeout(() => {
            setIsLoadingPath(true);
            fetchDocumentPath(props.item.key, { params: { notShowLoading: true } })
              .then((path) =>
                props.pathInfo.setPathForItem(
                  props.item.key,
                  `${DEFAULT_FOLDER_CREATION}${path}/${props.item.text.replace(/\.docx$/, "")}.docx`
                )
              )
              .finally(() => setIsLoadingPath(false));
          }, 1000)
        );
      }
    },
    [props.item, props.pathInfo]
  );

  useEffect(() => {
    if (props.pathInfo.getPathForItem(props.item.key) && timeoutInst) {
      clearTimeout(timeoutInst);
      setTimeoutInst(null);
    }
  }, [props.pathInfo, timeoutInst]);

  const handleTooltipDeletion = useCallback(() => {
    clearTimeout(timeoutInst);
    setTimeoutInst(null);
    setTooltipPosition(null);
  }, []);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        opacity: props.item.hasReadPermissions === false ? 0.6 : 1,
        width: "100%",
        color: "black",
      }}
      onClick={(e) => {
        if (props.item.nodeType === "pagination")
          props.fetchExpandedNode(props.itemHierarchicalIndex, false, props.item.data.number + 1);
        else props.onClick?.(e);
      }}
      onKeyDown={() => console.log("")}
    >
      {tooltipPosition && (
        <Tooltip positionY={tooltipPosition.positionY} positionX={tooltipPosition.positionX}>
          {props.pathInfo.getPathForItem(props.item.key) ?? documentName}
          {isLoadingPath && <Spinner size={SpinnerSize.large} id="spinner" />}
        </Tooltip>
      )}
      {props.isOutlineRibbon || props.isOpenOutlineRibbon ? (
        props.item.key !== "key" ? (
          <IconButton
            id={generateIdentifier(props.item.text, "certification_button")}
            className={
              props.documentPartWhithStatus?.length > 0
                ? iconCertification(props.item, props.documentPartWhithStatus)
                : "loading_status"
            }
            menuProps={props.item.hasReadPermissions && props.certificationOptions(props.item)}
            onRenderMenuIcon={RenderMenuIcon}
            style={{ color: "var(--color-black)", float: "right", height: "20px" }}
          />
        ) : null
      ) : (
        <span style={{ color: "blue" }} className={iconClassName(props.item)} key={`${props.item.key}_label`} />
      )}

      <span
        className={props.isSelected && props.item.hasReadPermissions ? "documentTreeBold" : "documentTreeRegular"}
        style={{
          cursor: "pointer",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          flex: 1,
        }}
        key={`${props.item.key}_text`}
        aria-label={generateIdentifier(props.item.text, "group")}
        onMouseOver={handleTooltipCreation}
        onMouseOut={handleTooltipDeletion}
      >
        {documentName}
      </span>
      {!props.isDraggable && multiLinked && props.item.hasReadPermissions && (
        <span className="k-icon k-i-link-vertical k-rotate-45 k-icon-sm" />
      )}
      {menuProps && (
        <IconButton
          id={generateIdentifier(props.item.text, "action_button")}
          iconProps={{ iconName: "MoreVertical" }}
          menuProps={menuProps}
          onRenderMenuIcon={RenderMenuIcon}
          style={{ color: "var(--color-black)", float: "right", height: "20px" }}
        />
      )}
    </div>
  );
};
